import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, notification, Upload } from 'antd';
import { FileExcelOutlined, SearchOutlined } from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { useIntl } from 'react-intl';
import client from '../../../config/GraphQLApolloClient';
import { MUTATION_UPLOAD_PARTNER_LIST } from '../obtMutations';
import { validatePartnerListFile } from '../utils/obtPartnerListValidator';

// ─── Column definitions ───────────────────────────────────────────────────────

const COL_DEFS: ColDef[] = [
    { field: 'partnerno', headerName: 'Partner No', flex: 1, minWidth: 120 },
    { field: 'companycode', headerName: 'Company Code', flex: 1, minWidth: 120 },
    { field: 'sysid', headerName: 'System', flex: 1, minWidth: 100 },
    { field: 'category', headerName: 'Category', flex: 1, minWidth: 100 },
];
const DEFAULT_COL: ColDef = { resizable: true, sortable: true };

// ─── Props ────────────────────────────────────────────────────────────────────

export interface FileSearchRow {
    partnerno: string;
    companycode: string;
    sysid: string;
    category: string;
}

type ObtSearchByFileModalProps = {
    open: boolean;
    onClose: () => void;
    onApply: (rows: FileSearchRow[]) => void;
};

// ─── Component ────────────────────────────────────────────────────────────────

const ObtSearchByFileModal: React.FC<ObtSearchByFileModalProps> = ({ open, onClose, onApply }) => {
    const intl = useIntl();
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState<any[]>([]);
    const [fileName, setFileName] = useState<string>('');
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const pendingFile = useRef<File | null>(null);
    const hasSelectedFile = fileName.trim().length > 0;

    // Store file and trigger upload on next render
    const handleBeforeUpload = useCallback((file: any) => {
        if (hasSelectedFile) {
            notification.warning({
                message: intl.formatMessage({
                    id: 'obt.searchByFile.clearBeforeSelect',
                    defaultMessage: 'Please clear the current file before selecting another one.'
                })
            });
            return Upload.LIST_IGNORE;
        }

        pendingFile.current = file;
        setFileName(file.name);
        setUploading(true);
        return false; // prevent default upload
    }, [hasSelectedFile, intl]);

    // Auto-upload when a file is selected
    useEffect(() => {
        if (!uploading || !pendingFile.current) return;
        const file = pendingFile.current;
        pendingFile.current = null;

        const run = async () => {
            // ── Validate before sending to backend ──
            const validation = await validatePartnerListFile(file);
            if (!validation.valid) {
                setValidationErrors(validation.errors);
                setUploading(false);
                return;
            }
            setValidationErrors([]);

            const reader = new FileReader();
            reader.onload = () => {
                const base64 = (reader.result as string).split(',')[1];
                client
                    .mutate({ mutation: MUTATION_UPLOAD_PARTNER_LIST, variables: { fileBase64: base64, fileName: file.name } })
                    .then((res) => {
                        const data = res?.data?.obtUploadExcelFile ?? [];
                        setUploadResult(data);
                        if (data.length === 0) notification.warning({ message: 'No partners found in file.' });
                    })
                    .catch((err) => {
                        console.error('Upload failed:', err);
                        notification.error({ message: intl.formatMessage({ id: 'obt.update.upload.error', defaultMessage: 'Upload failed.' }) });
                    })
                    .finally(() => setUploading(false));
            };
            reader.onerror = () => {
                notification.error({ message: intl.formatMessage({ id: 'obt.update.upload.error', defaultMessage: 'Upload failed.' }) });
                setUploading(false);
            };
            reader.readAsDataURL(file);
        };

        run();
    }, [uploading, intl]);

    const handleApply = useCallback(() => {
        if (uploadResult.length === 0) return;
        onApply(uploadResult);
        setUploadResult([]);
        setFileName('');
    }, [uploadResult, onApply]);

    const handleClear = useCallback(() => {
        pendingFile.current = null;
        setUploadResult([]);
        setFileName('');
        setValidationErrors([]);
    }, []);

    const handleCancel = useCallback(() => {
        onClose();
        pendingFile.current = null;
        setUploadResult([]);
        setFileName('');
        setValidationErrors([]);
    }, [onClose]);

    return (
        <Modal
            title={intl.formatMessage({ id: 'obt.searchByFile', defaultMessage: 'Search by File' })}
            open={open}
            onCancel={handleCancel}
            width={700}
            footer={[
                <Button key="clear" onClick={handleClear} disabled={!hasSelectedFile}>
                    {intl.formatMessage({ id: 'do-clear', defaultMessage: 'Clear' })}
                </Button>,
                <Button key="apply" type="primary" disabled={uploadResult.length === 0} onClick={handleApply} icon={<SearchOutlined />}>
                    {intl.formatMessage({ id: 'obt.searchByFile.apply', defaultMessage: 'Apply & Search' })}
                    {uploadResult.length > 0 ? ` (${uploadResult.length})` : ''}
                </Button>,
            ]}
            destroyOnHidden={true}
        >
            <p style={{ color: 'rgba(0,0,0,0.65)', marginBottom: 16 }}>
                {intl.formatMessage({ id: 'obt.searchByFile.description', defaultMessage: 'Upload an Excel file with columns: System, Partner No, Company Code, Category. The grid will filter to show only the uploaded partners.' })}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <Upload accept=".xlsx,.xls,.xlsm" beforeUpload={handleBeforeUpload} showUploadList={false} maxCount={1} disabled={hasSelectedFile}>
                    <Button icon={<FileExcelOutlined />} loading={uploading} disabled={hasSelectedFile}>
                        {intl.formatMessage({ id: 'obt.update.upload.selectFile', defaultMessage: 'Select Excel File' })}
                    </Button>
                </Upload>
                {fileName && <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>{fileName}</span>}
            </div>
            {validationErrors.length > 0 && (
                <Alert
                    type="error"
                    showIcon={true}
                    message={intl.formatMessage({ id: 'obt.upload.validation.title', defaultMessage: 'Invalid file format' })}
                    description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
                        </ul>
                    }
                    style={{ marginBottom: 12 }}
                />
            )}
            {uploadResult.length > 0 && (
                <div className="ag-theme-balham" style={{ height: 300, width: '100%' }}>
                    <AgGridReact
                        rowData={uploadResult}
                        columnDefs={COL_DEFS}
                        defaultColDef={DEFAULT_COL}
                        animateRows={true}
                        pagination={true}
                        paginationPageSize={50}
                    />
                </div>
            )}
        </Modal>
    );
};

export default ObtSearchByFileModal;
